import { Fragment, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { Divider } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import CalendarIcon from '../../../Assets/calendar.svg';
import MoneyIcon from '../../../Assets/money.svg';
import ListItem from '../../../Components/ListItem';
import { textCategory } from '../../../Components/Reservation/constants';
import ResponsiveText from '../../../Components/ResponsiveText';
import { getEventPriceRange } from '../../../Helper';
import DateHelper from '../../../Helper/Date';
import { useGetEventDetailsQuery } from '../../../RTK/api/events';
import themeStyle, { globalSpacing } from '../../../Theme/styles';
import {
  ids as platformStyleIds,
  styles as platformStyles,
} from '../../../Theme/styles/platform';

type Props = {
  eventId: string;
};

const Overview = ({ eventId }: Props) => {
  const { data, isLoading } = useGetEventDetailsQuery(
    { payload: eventId },
    { skip: !eventId },
  );

  const hide = isLoading || lodashIsEmpty(data);

  const list = useMemo(() => {
    if (hide) return [];
    const { isLastStart, startFormatted, start } =
      DateHelper.getEventScheduleInfo(
        data.start_date,
        data.end_date,
        data.time_slots,
        data.available_days,
      );
    return [
      {
        name: isLastStart
          ? startFormatted
          : DateHelper.formatDateWithText(
              start,
              data.end_date,
              data.available_days,
            ),
        icon: { leftIcon: { svg: CalendarIcon, size: 20 } },
      },
      {
        name: DateHelper.formatTimeRangeArray(data.time_slots),
        icon: { leftIcon: { name: 'clock-outline', size: 20 } },
      },
      {
        name: `${getEventPriceRange(data.tickets)} per ticket`,
        icon: { leftIcon: { svg: MoneyIcon, size: 20 } },
      },
      {
        name: data.location_address,
        icon: { leftIcon: { name: 'pin-outline', size: 20 } },
      },
    ];
  }, [
    data.available_days,
    data.end_date,
    data.location_address,
    data.start_date,
    data.tickets,
    data.time_slots,
    hide,
  ]);

  if (hide) null;

  return (
    <View>
      <View
        dataSet={{ media: platformStyleIds.webOnly }}
        style={platformStyles.webOnly}
      >
        <ResponsiveText category={textCategory.HEADING} style={themeStyle.bold}>
          Event Details
        </ResponsiveText>
      </View>
      <View style={styles.marginBottomOffset}>
        {list.map((l, i) => (
          <Fragment key={i}>
            <ListItem
              contentContainerStyle={themeStyle.pageVerticalSpacingMedium}
              title={
                <ResponsiveText category={textCategory.BODY}>
                  {l.name}
                </ResponsiveText>
              }
              {...l.icon}
            />
            {i !== list.length - 1 && <Divider style={themeStyle.divider} />}
          </Fragment>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  marginBottomOffset: {
    marginBottom: -(globalSpacing / 2),
  },
});

export default Overview;
